import React, { useEffect, useRef, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import Navbar from "../../components/navbar/Navbar";
import "react-toastify/dist/ReactToastify.css";
import "../../sass/createorder.scss";
import { apiInstance } from "../../config/apiConfig";
import { TailSpin } from "react-loader-spinner";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

const CreateProject = () => {
  const [images, setImages] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  // Build previews + cleanup object URLs
  useEffect(() => {
    const urls = images.map((file) => URL.createObjectURL(file));
    setPreviews(urls);

    return () => {
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [images]);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);

    const valid = files.filter((f) => ALLOWED_TYPES.includes(f.type));
    const invalidCount = files.length - valid.length;

    if (invalidCount > 0) {
      toast.error("Some files were ignored (only images allowed).");
    }

    if (valid.length === 0) return;

    // functional update (prevents missing files)
    setImages((prev) => [...prev, ...valid]);
  };

  const handleFileChange = (e) => {
    addFiles(e.target.files);

    // allow selecting the same file again
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveImage = (index) => {
    setImages((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0 && fileInputRef.current) fileInputRef.current.value = "";
      return next;
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    addFiles(e.dataTransfer.files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !note.trim() || images.length === 0) {
      toast.error("Please fill all fields and add at least one image.");
      return;
    }

    setIsLoading(true);

    const formData = new FormData();
    formData.append("title", title.trim());
    formData.append("note", note.trim());
    images.forEach((image) => formData.append("images", image));

    try {
      await apiInstance.post("/api/order/create", formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          // Content-Type ko manually set mat karo; axios auto boundary set karega
        },
      });

      toast.success("Project created successfully!");
      setTitle("");
      setNote("");
      setImages([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error("Error submitting form:", error);

      const msg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to create project.";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="create-project-container">
      <Navbar />

      <h2 className="text-2xl font-bold mb-4">Create New Project</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div
          className="form-section"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
          style={{
            border: "2px dashed #ccc",
            textAlign: "center",
            padding: "20px",
            position: "relative",
          }}
        >
          <label htmlFor="images" className="block mb-2 cursor-pointer">
            <div style={{ fontSize: "48px" }}>+</div>
          </label>

          <input
            ref={fileInputRef}
            id="images"
            type="file"
            multiple
            onChange={handleFileChange}
            className="input input-bordered w-full"
            accept="image/jpeg,image/png,image/gif,image/webp"
            style={{ display: "none" }}
          />

          <p className="text-sm text-gray-600">Add Reference Images*</p>

          <div className="image-preview-container">
            {previews.map((src, index) => (
              <div
                key={index}
                className="image-preview"
                style={{
                  position: "relative",
                  display: "inline-block",
                  marginRight: "10px",
                }}
              >
                <img
                  src={src}
                  alt="Preview"
                  style={{ width: "100px", height: "100px", objectFit: "cover" }}
                />

                <button
                  type="button"
                  onClick={() => handleRemoveImage(index)}
                  style={{
                    position: "absolute",
                    top: "0px",
                    right: "0px",
                    background: "rgba(255, 255, 255, 0.7)",
                    borderRadius: "50%",
                    width: "24px",
                    height: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "none",
                    cursor: "pointer",
                    outline: "none",
                  }}
                  aria-label="Remove image"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    stroke="red"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          {isLoading && (
            <div
              style={{
                position: "fixed",
                bottom: "20px",
                right: "20px",
                zIndex: 1000,
              }}
            >
              <TailSpin color="red" height={50} width={50} />
            </div>
          )}

          <label htmlFor="title" className="block mb-2">
            Title*
          </label>

          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Add a title for your model e.g. Halo Prism"
            className="input input-bordered w-full"
          />
        </div>

        <div className="form-section">
          <label htmlFor="note" className="block mb-2">
            Leave a note*
          </label>

          <textarea
            id="note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add any special instructions here"
            className="input input-bordered w-full"
          />
        </div>

        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? "Creating..." : "Create Project"}
        </button>
      </form>

      <ToastContainer
        position="bottom-center"
        autoClose={2000}
        hideProgressBar
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default CreateProject;
